/**
 * Panel Policy — Lot 4 du chantier politiques-host.
 *
 * Affiche :
 *   1. La policy effective publiée par le host (résolue GPO > registre > config > default)
 *   2. L'état temps réel d'access granulaire (view/control/privacyScreen/localInputBlock)
 *   3. Boutons pour déclencher access:request (popup côté utilisateur)
 *   4. RDS multi-session : stub pour Lot ultérieur si rdsTargetMode = rds_user_select
 *
 * Le panel est read-only sur la policy (modification = via GPO côté serveur AD ou
 * éditeur de registre côté host) et write sur les access (boutons « Demander »).
 */
import { Show, For, createMemo, createSignal, onCleanup, type Component } from "solid-js";
import { PanelLayout, type SubTab, type PanelStatus } from "@/shared/panel-base";
import { useWs } from "@/shared/ws";
import { registerPanel } from "@/panel-registry";
import { encryptCredentialPassword } from "@/shared/credential-encryption";
import { CredentialInput, type CredentialInputResult } from "@/shared/credential-input";
import { sealCredentialVaultCredentialForAgent } from "@/shared/credential-vault-client";
import type {
  AccessLevel,
  AccessSnapshot,
  AccessState,
  AgentCredentialPublicKey,
  EncryptedCredential,
  HostPolicy,
  RdsSession,
} from "@/shared/types";
import "./policy.css";

const CREDENTIAL_KEY_REQUEST_TIMEOUT_MS = 5000;

const ACCESS_LEVELS: { id: AccessLevel; label: string; desc: string }[] = [
  { id: "view", label: "View — voir l'écran", desc: "Capture WGC + diffusion vidéo." },
  { id: "control", label: "Control — clavier/souris", desc: "Injection SendInput, clipboard sync." },
  { id: "privacyScreen", label: "Privacy Screen — écran noir local", desc: "Overlay WDA_EXCLUDEFROMCAPTURE." },
  { id: "localInputBlock", label: "Local Input Block", desc: "BlockInput() — gel clavier/souris local." },
];

const POLICY_GROUPS: {
  title: string;
  fields: { key: keyof HostPolicy; label: string; type: "string" | "bool" | "number" | "list" | "enum" }[];
}[] = [
  {
    title: "Consentement",
    fields: [
      { key: "viewConsentMode", label: "View consent mode", type: "enum" },
      { key: "controlConsentMode", label: "Control consent mode", type: "enum" },
      { key: "consentTimeoutSeconds", label: "Consent timeout (s)", type: "number" },
      { key: "consentTimeoutAction", label: "Timeout action", type: "enum" },
    ],
  },
  {
    title: "Privacy & input",
    fields: [
      { key: "allowPrivacyScreen", label: "Privacy screen autorisé", type: "bool" },
      { key: "allowLocalInputBlock", label: "Input block autorisé", type: "bool" },
      { key: "privacyScreenRequiresConsent", label: "Consentement requis (privacy)", type: "bool" },
      {
        key: "localInputBlockRequiresPrivacyScreen",
        label: "Input block nécessite privacy screen",
        type: "bool",
      },
      { key: "powerHoldMaxMinutes", label: "Power hold max (min)", type: "number" },
    ],
  },
  {
    title: "RDS / multi-session",
    fields: [{ key: "rdsTargetMode", label: "Cible RDS", type: "enum" }],
  },
  {
    title: "Chat & fichiers",
    fields: [
      { key: "allowUserOperatorChat", label: "Chat autorisé", type: "bool" },
      { key: "chatAvailableBeforeConsent", label: "Chat avant consentement", type: "bool" },
    ],
  },
  {
    title: "Connectivité",
    fields: [
      { key: "allowedRelayHosts", label: "Relais autorisés", type: "list" },
      { key: "blockedDomains", label: "Domaines bloqués", type: "list" },
    ],
  },
  {
    title: "Écran virtuel (mode test)",
    fields: [
      { key: "allowVirtualOperatorDisplay", label: "Mode écran caché autorisé", type: "bool" },
      { key: "virtualDisplayProvider", label: "Provider d'écran virtuel", type: "enum" },
      { key: "virtualDisplayRequiresPrivacyScreen", label: "Nécessite privacy screen", type: "bool" },
      { key: "allowWindowMoveToVirtualDisplay", label: "Déplacer fenêtres lancées", type: "bool" },
      {
        key: "allowMoveExistingWindowsToVirtualDisplay",
        label: "Déplacer fenêtres existantes",
        type: "bool",
      },
      { key: "restoreMovedWindowsOnStop", label: "Restaurer fenêtres à l'arrêt", type: "bool" },
    ],
  },
  {
    title: "Audit",
    fields: [
      { key: "auditPolicyDecisions", label: "Audit des décisions", type: "bool" },
      { key: "chatTranscriptAuditMode", label: "Mode audit chat", type: "enum" },
    ],
  },
];

const PolicyPanel: Component<{ vaultDefaults?: Record<string, string> }> = (props) => {
  const ws = useWs();

  const policy = createMemo<HostPolicy | null>(() => ws.state.hostPolicy ?? null);
  const access = createMemo<AccessSnapshot | null>(() => ws.state.access ?? null);

  // Lot 6 — dialog credentials pour RDP Shadow.
  const [shadowDialogOpen, setShadowDialogOpen] = createSignal(false);
  const [shadowTargetSession, setShadowTargetSession] = createSignal<number | null>(null);
  const [shadowResult, setShadowResult] = createSignal<CredentialInputResult>({ mode: "manual", ready: false });
  const [shadowControl, setShadowControl] = createSignal(true);
  const [shadowError, setShadowError] = createSignal<string | null>(null);
  const [shadowActiveId, setShadowActiveId] = createSignal<string | null>(null);

  // Lot 7 — dialog credentials pour Unattended Access (aucun user loggé).
  const [unattendedDialogOpen, setUnattendedDialogOpen] = createSignal(false);
  const [unattendedResult, setUnattendedResult] = createSignal<CredentialInputResult>({
    mode: "manual",
    ready: false,
  });
  const [unattendedError, setUnattendedError] = createSignal<string | null>(null);
  const [unattendedActiveId, setUnattendedActiveId] = createSignal<string | null>(null);
  // vaultCredentialId/vaultLoopbackBusy supprimés : fusionnés dans <CredentialInput>

  // Gate consentement : ne demander la clé publique agent QU'APRÈS consentement
  // de session accordé (ou accès unattended/headless explicitement autorisé).
  // Évite le drop relais → erreur `agent:credential_key_error` → faux timeout.
  // Option A : la prise console (login clavier) doit aussi être proposée quand la
  // session console est VERROUILLÉE (user loggé mais lock), pas seulement quand
  // aucun user n'est loggé. Réutilise le flux unattended:request_access existant.
  const anySessionLocked = () => (ws.state.rdsSessions ?? []).some((s) => s.locked === true);
  const credentialRequestAllowed = () =>
    ws.state.supportApproved === true ||
    ((ws.state.noUserLoggedIn || anySessionLocked()) && ws.state.hostPolicy?.unattendedAccessAllowed === true);

  const requestCredentialKeyOnce = (): Promise<AgentCredentialPublicKey> => {
    if (!ws.isOpen()) return Promise.reject(new Error("Connexion WS indisponible."));

    const requestId = crypto.randomUUID();
    return new Promise((resolve, reject) => {
      let off = () => {};
      const timer = window.setTimeout(() => {
        off();
        reject(new Error("Clé publique agent non reçue."));
      }, CREDENTIAL_KEY_REQUEST_TIMEOUT_MS);
      off = ws.on(
        (m) => {
          const msg = m as { type?: string; requestId?: string };
          return (
            (msg.type === "agent:credential_key" || msg.type === "agent:credential_key_error") &&
            (!msg.requestId || msg.requestId === requestId)
          );
        },
        (m) => {
          window.clearTimeout(timer);
          off();
          const msg = m as { type?: string; message?: string };
          if (msg.type === "agent:credential_key_error") {
            reject(new Error(msg.message || "Clé publique agent refusée par le relais."));
            return;
          }
          resolve(m as AgentCredentialPublicKey);
        },
      );
      ws.send({ type: "agent:credential_key_request", requestId } as unknown as Parameters<typeof ws.send>[0]);
    });
  };

  const requestCredentialKey = async (): Promise<AgentCredentialPublicKey> => {
    if (!credentialRequestAllowed()) {
      throw new Error("Consentement de session requis avant de demander la clé publique agent.");
    }
    try {
      return await requestCredentialKeyOnce();
    } catch (err) {
      // Retry UNE fois : couvre le flapping transitoire du consentement.
      if (!credentialRequestAllowed()) throw err;
      return requestCredentialKeyOnce();
    }
  };

  ws.on(
    (m) => (m as { type?: string }).type === "unattended:result",
    (m) => {
      const r = m as {
        type: "unattended:result";
        ok: boolean;
        shadowId?: string;
        error?: string;
        consoleAction?: string;
      };
      // Résultat d'une action « libérer la console » (logoff/verrouiller) : géré
      // par son handler dédié plus bas, pas un open de session unattended.
      if (r.consoleAction) return;
      if (r.ok) {
        setUnattendedActiveId(r.shadowId || null);
        setUnattendedError(null);
        setUnattendedDialogOpen(false);
        setUnattendedResult({ mode: "manual", ready: false });
        setSecureRdsMode(false);
      } else {
        setUnattendedError(r.error || "Erreur inconnue");
        setUnattendedResult({ mode: "manual", ready: false });
      }
    },
  );

  // ── Prise de console : libérer l'écran d'un user déjà connecté (logoff/verrouiller) ──
  // Réutilise le message unattended:request_access (forwardé opaque par le relai)
  // avec un champ consoleAction + targetSessionId, SANS credentials : l'agent ne
  // fait que WTSLogoffSession (logoff) ou WTSDisconnectSession (verrouiller).
  const [consoleActionBusy, setConsoleActionBusy] = createSignal<number | null>(null);
  const [consoleActionMsg, setConsoleActionMsg] = createSignal<string | null>(null);
  const [consoleActionError, setConsoleActionError] = createSignal(false);
  // Corrélation requestId : ne traiter QUE le résultat de NOTRE action — sinon le
  // résultat d'un autre opérateur agissant en parallèle remettrait notre busy à
  // null et afficherait le mauvais message (cross-talk multi-op).
  let consoleActionReqId: string | null = null;

  const offConsoleAction = ws.on(
    (m) => {
      const r = m as { type?: string; consoleAction?: string; requestId?: string };
      return r.type === "unattended:result" && Boolean(r.consoleAction) && r.requestId === consoleActionReqId;
    },
    (m) => {
      const r = m as { type: "unattended:result"; ok: boolean; consoleAction?: string; error?: string };
      consoleActionReqId = null;
      setConsoleActionBusy(null);
      setConsoleActionError(!r.ok);
      if (r.ok) {
        setConsoleActionMsg(
          r.consoleAction === "logoff"
            ? "Fermeture demandée — l'écran se libère, prenez la console."
            : "Session verrouillée — l'écran est libéré, prenez la console.",
        );
      } else {
        setConsoleActionMsg(`Échec : ${r.error || "erreur inconnue"}`);
      }
    },
  );
  onCleanup(offConsoleAction);

  const freeConsole = (session: RdsSession, action: "logoff" | "lock") => {
    const who = `${session.domainName ? `${session.domainName}\\` : ""}${session.userName ?? "?"}`;
    if (action === "logoff") {
      const ok = window.confirm(
        `Fermer la session de ${who} (#${session.sessionId}) ?\n\n` +
          `Action DESTRUCTIVE : la session et ses programmes non enregistrés seront perdus.`,
      );
      if (!ok) return;
    }
    setConsoleActionError(false);
    setConsoleActionMsg(null);
    setConsoleActionBusy(session.sessionId);
    const requestId = crypto.randomUUID();
    consoleActionReqId = requestId;
    ws.send({
      type: "unattended:request_access",
      requestId,
      consoleAction: action,
      targetSessionId: session.sessionId,
      // Confirmation destructive (logoff) : l'agent REFUSE le logoff sans ce champ
      // (le window.confirm seul est contournable). Horodaté après confirmation.
      ...(action === "logoff" ? { userConfirmedAt: new Date().toISOString() } : {}),
    } as unknown as Parameters<typeof ws.send>[0]);
  };

  // Mode RDS sécurisé — « WSS dans RDS », PAS shadow, PAS mstsc, PAS keyboard.
  // Flux `method:"rds_loopback"` : l'opérateur fournit ses creds dans le dialogue ;
  // l'agent ouvre un RDP HEADLESS en LOCAL (client IronRDP → 127.0.0.1:3389, NLA
  // locale) avec ces creds → Windows crée une session RDS ISOLÉE (bureau virtuel,
  // marche sans écran physique). Le pipeline WSS (WgcCapture/Helper) est ensuite
  // re-ciblé sur cette session via WTSQueryUserToken (vraie session TS).
  const [secureRdsMode, setSecureRdsMode] = createSignal(false);
  const openSecureRds = () => {
    setSecureRdsMode(true);
    setUnattendedError(null);
    setUnattendedResult({ mode: "manual", ready: false });
    setUnattendedDialogOpen(true);
  };

  // openSecureRdsFromVault supprimé : fusionné dans <CredentialInput> du dialog.

  const submitUnattended = async () => {
    const result = unattendedResult();
    const ok = result.ready || (result.mode === "volt" && result.credentialId);
    if (!ok) {
      setUnattendedError("Identifiants requis (ou sélectionner un credential VOLT).");
      return;
    }
    setUnattendedError(null);
    try {
      let credential: EncryptedCredential | undefined;
      let username = result.username ?? "";
      let domain = result.domain ?? "";

      if (result.mode === "volt") {
        if (result.sealed) {
          credential = result.sealed;
        } else if (result.credentialId) {
          const credentialKey = await requestCredentialKey();
          const sealed = await sealCredentialVaultCredentialForAgent(result.credentialId, {
            agentPublicKey: credentialKey,
            sessionCode: ws.state.sessionCode ?? undefined,
            purpose: "rds_loopback",
          });
          credential = sealed.credential ?? sealed.sealed;
          if (sealed.username) username = sealed.username;
          if (sealed.domain) domain = sealed.domain;
        }
        if (!credential) throw new Error("Credential vault : secret agent absent.");
      } else {
        if (!result.password) {
          setUnattendedError("Mot de passe requis.");
          return;
        }
        const credentialKey = await requestCredentialKey();
        credential = await encryptCredentialPassword(credentialKey, result.password);
      }

      setUnattendedResult({ mode: "manual", ready: false });
      ws.send({
        type: "unattended:request_access",
        requestId: crypto.randomUUID(),
        username,
        domain,
        credential,
        ...(secureRdsMode() ? { method: "rds_loopback", userConfirmedAt: new Date().toISOString() } : {}),
      } as unknown as Parameters<typeof ws.send>[0]);
    } catch (err) {
      setUnattendedResult({ mode: "manual", ready: false });
      setUnattendedError(err instanceof Error ? err.message : "Chiffrement du secret impossible.");
    }
  };

  const stopUnattended = () => {
    const id = unattendedActiveId();
    if (!id) return;
    ws.send({ type: "unattended:stop", shadowId: id } as unknown as Parameters<typeof ws.send>[0]);
    setUnattendedActiveId(null);
  };

  // Listener pour rds:shadow_result.
  ws.on(
    (m) => (m as { type?: string }).type === "rds:shadow_result",
    (m) => {
      const r = m as { type: "rds:shadow_result"; ok: boolean; shadowId?: string; error?: string };
      if (r.ok) {
        setShadowActiveId(r.shadowId || null);
        setShadowError(null);
        setShadowDialogOpen(false);
        // Zero-out le password en mémoire dès succès.
        setShadowResult({ mode: "manual", ready: false });
      } else {
        setShadowError(r.error || "Erreur inconnue");
        setShadowResult({ mode: "manual", ready: false });
      }
    },
  );

  const openShadowDialog = (sessionId: number) => {
    setShadowTargetSession(sessionId);
    setShadowError(null);
    setShadowResult({ mode: "manual", ready: false });
    setShadowDialogOpen(true);
  };

  const submitShadow = async () => {
    const sessionId = shadowTargetSession();
    if (sessionId === null) return;
    const result = shadowResult();
    const ok = result.ready || (result.mode === "volt" && result.credentialId);
    if (!ok) {
      setShadowError("Identifiants requis (ou sélectionner un credential VOLT).");
      return;
    }
    setShadowError(null);
    try {
      let credential: EncryptedCredential | undefined;
      let username = result.username ?? "";
      let domain = result.domain ?? "";

      if (result.mode === "volt") {
        if (result.sealed) {
          credential = result.sealed;
        } else if (result.credentialId) {
          const credentialKey = await requestCredentialKey();
          const sealed = await sealCredentialVaultCredentialForAgent(result.credentialId, {
            agentPublicKey: credentialKey,
            sessionCode: ws.state.sessionCode ?? undefined,
            purpose: "rdp_shadow",
          });
          credential = sealed.credential ?? sealed.sealed;
          if (sealed.username) username = sealed.username;
          if (sealed.domain) domain = sealed.domain;
        }
        if (!credential) throw new Error("Credential vault : secret agent absent.");
      } else {
        if (!result.password) {
          setShadowError("Mot de passe requis.");
          return;
        }
        const credentialKey = await requestCredentialKey();
        credential = await encryptCredentialPassword(credentialKey, result.password);
      }

      setShadowResult({ mode: "manual", ready: false });
      ws.send({
        type: "rds:shadow_start",
        requestId: crypto.randomUUID(),
        username,
        domain,
        credential,
        targetSessionId: sessionId,
        control: shadowControl(),
      } as unknown as Parameters<typeof ws.send>[0]);
    } catch (err) {
      setShadowResult({ mode: "manual", ready: false });
      setShadowError(err instanceof Error ? err.message : "Chiffrement du secret impossible.");
    }
  };

  const stopShadow = () => {
    const id = shadowActiveId();
    if (!id) return;
    ws.send({ type: "rds:shadow_stop", shadowId: id } as unknown as Parameters<typeof ws.send>[0]);
    setShadowActiveId(null);
  };

  const status = (): PanelStatus | undefined => {
    if (!ws.state.nativeAgentOnline)
      return { text: "Hôte hors-ligne — policy figée à la dernière valeur connue.", level: "warn" };
    if (!policy()) return { text: "Aucune policy publiée — hôte sur une version antérieure au Lot 1.", level: "info" };
    return undefined;
  };

  const requestAccess = (level: AccessLevel) => {
    ws.send({ type: "access:request", level } as unknown as Parameters<typeof ws.send>[0]);
  };

  const renderValue = (key: keyof HostPolicy, type: "string" | "bool" | "number" | "list" | "enum") => {
    const v = policy()?.[key];
    if (v === undefined || v === null) return <span class="value">—</span>;
    if (type === "bool") {
      const b = Boolean(v);
      return <span class={`value bool-${b ? "true" : "false"}`}>{b ? "✓ activé" : "✗ désactivé"}</span>;
    }
    if (type === "list") {
      const arr = Array.isArray(v) ? (v as string[]) : [];
      if (arr.length === 0) return <span class="value">∅ (aucune)</span>;
      return <span class="value">{arr.join(", ")}</span>;
    }
    return <span class="value">{String(v)}</span>;
  };

  const renderVirtualProviderBadge = () => {
    const provider = policy()?.virtualDisplayProvider;
    if (!provider || provider === "none") return null;
    if (!policy()?.allowVirtualOperatorDisplay) {
      return (
        <span class="policy-badge policy-badge--lab">
          Provider défini ({provider}) mais <code>allowVirtualOperatorDisplay</code> = false
        </span>
      );
    }
    if (provider === "virtualdrivers-vdd" || provider === "vdd") {
      return (
        <span class="policy-badge policy-badge--lab">
          Provider lab actif : <code>{provider}</code> — test seulement
        </span>
      );
    }
    if (provider === "remotews-idd") {
      return (
        <span class="policy-badge policy-badge--internal">
          Provider natif RemoteWS IdD — expérimental, conforme au pilotage interne
        </span>
      );
    }
    return null;
  };

  const renderSource = (_key: keyof HostPolicy) => {
    const src = policy()?.source;
    if (!src) return null;
    const cssClass = src === "config.json" ? "config" : src === "defaults" ? "default" : src;
    return <span class={`source ${cssClass}`}>{src}</span>;
  };

  const policyTab: SubTab = {
    id: "effective",
    label: "Policy effective",
    render: () => (
      <Show
        when={policy()}
        fallback={
          <div class="policy-empty">
            Aucune <code>hostPolicy</code> publiée par cet hôte. Vérifiez la version de l'agent (≥ Lot 1) et
            l'enregistrement via heartbeat.
          </div>
        }
      >
        <div class="policy-panel">
          <Show when={policy()?.virtualDisplayProvider}>{renderVirtualProviderBadge()}</Show>
          <For each={POLICY_GROUPS}>
            {(group) => (
              <div class="policy-card">
                <h3>{group.title}</h3>
                <For each={group.fields}>
                  {(f) => (
                    <div class="policy-row">
                      <span class="key">{f.label}</span>
                      {renderValue(f.key, f.type)}
                      {renderSource(f.key)}
                    </div>
                  )}
                </For>
              </div>
            )}
          </For>
        </div>
      </Show>
    ),
  };

  const accessStateLabel = (s: AccessState | undefined): string => {
    if (!s) return "inconnu";
    return s;
  };

  const canRequest = (level: AccessLevel, st: AccessState | undefined): boolean => {
    if (!ws.state.nativeAgentOnline) return false;
    // Pas la peine de redemander si déjà approved ou not_required ; idem si pending.
    if (st === "approved" || st === "not_required" || st === "pending" || st === "on") return false;
    // PrivacyScreen / inputBlock : interdire le bouton si la policy le refuse.
    if (level === "privacyScreen") {
      if (policy()?.allowPrivacyScreen === false) return false;
      if (policy()?.privacyScreenAllowed === false) return false;
      return true;
    }
    if (level === "localInputBlock") {
      const allowed = policy()?.allowLocalInputBlock !== false && policy()?.localInputBlockAllowed !== false;
      if (!allowed) return false;

      // Selon policy, BlockInput peut exiger que privacyScreen soit actif.
      const privacyModeOn = access()?.privacyScreen === "on";
      if (policy()?.localInputBlockRequiresPrivacyScreen && !privacyModeOn) {
        return false;
      }
      return true;
    }
    return true;
  };

  const canStop = (level: AccessLevel, st: AccessState | undefined): boolean => {
    if (level !== "privacyScreen" && level !== "localInputBlock") return false;
    return st === "on";
  };

  const stopAccess = (level: AccessLevel) => {
    ws.send({ type: "access:stop", level } as unknown as Parameters<typeof ws.send>[0]);
  };

  const accessTab: SubTab = {
    id: "access",
    label: "Access (temps réel)",
    render: () => (
      <div class="policy-panel">
        {/* Lot 8 — état explicite quand aucun user n'est loggé */}
        <Show when={ws.state.noUserLoggedIn || anySessionLocked()}>
          <div
            class="policy-card"
            style={{ background: "rgba(96, 165, 250, 0.08)", "border-color": "rgba(96, 165, 250, 0.3)" }}
          >
            <h3 style={{ color: "#60a5fa" }}>
              {ws.state.noUserLoggedIn ? "🔒 Aucun utilisateur connecté" : "🔒 Session verrouillée"}
            </h3>
            <p style={{ "font-size": "12px", color: "var(--text-dim)", margin: "4px 0 8px 0" }}>
              {ws.state.noUserLoggedIn
                ? "Ce poste n'a pas d'utilisateur interactivement connecté. Selon la policy, vous pouvez :"
                : "La session console est verrouillée. Selon la policy, vous pouvez reprendre la main en saisissant des identifiants (déverrouillage clavier) :"}
            </p>

            {/* Chemin 1 — voir la mire de login */}
            <Show when={policy()?.systemViewLoginScreenAllowed}>
              <p style={{ "font-size": "12px", color: "var(--text-dim)", margin: "4px 0" }}>
                • La capture vidéo montre l'écran de connexion Windows (vue seule).
              </p>
            </Show>

            {/* Chemin 2 — accès non assisté */}
            <Show when={policy()?.unattendedAccessAllowed}>
              <Show
                when={!unattendedActiveId()}
                fallback={
                  <div style={{ display: "flex", "align-items": "center", gap: "8px", margin: "6px 0" }}>
                    <span style={{ color: "#4ade80" }}>
                      ● Session active (<code>{unattendedActiveId()?.slice(0, 8)}…</code>)
                    </span>
                    <button onClick={stopUnattended}>Arrêter et logoff</button>
                  </div>
                }
              >
                <button
                  onClick={() => {
                    setSecureRdsMode(false);
                    setUnattendedError(null);
                    setUnattendedResult({ mode: "manual", ready: false });
                    setUnattendedDialogOpen(true);
                  }}
                  style={{
                    background: "var(--accent)",
                    color: "white",
                    padding: "6px 14px",
                    "font-size": "12px",
                    margin: "6px 0",
                  }}
                >
                  Demander accès non assisté
                </button>
              </Show>
            </Show>

            {/* Chemin 3 — blocage explicite si aucune policy ne permet d'agir */}
            <Show when={!policy()?.systemViewLoginScreenAllowed && !policy()?.unattendedAccessAllowed}>
              <small style={{ display: "block", "margin-top": "6px", color: "#f87171" }}>
                ⚠ La policy de ce poste interdit à la fois la vue de la mire de login (
                <code>systemViewLoginScreenAllowed = false</code>) et l'accès non assisté (
                <code>unattendedAccessAllowed = false</code>). Vous ne pouvez pas prendre la main tant qu'un utilisateur
                ne se connecte pas localement.
              </small>
            </Show>
          </div>
        </Show>

        <div class="policy-access-grid">
          <For each={ACCESS_LEVELS}>
            {(lvl) => {
              const st = () => access()?.[lvl.id];
              return (
                <div class="policy-access-cell">
                  <div class="level">{lvl.label}</div>
                  <div class={`state ${st() ?? ""}`}>{accessStateLabel(st())}</div>
                  <div style={{ "font-size": "11px", color: "var(--text-dim)", "margin-bottom": "8px" }}>
                    {lvl.desc}
                  </div>
                  <Show
                    when={canStop(lvl.id, st())}
                    fallback={
                      <button disabled={!canRequest(lvl.id, st())} onClick={() => requestAccess(lvl.id)}>
                        Demander
                      </button>
                    }
                  >
                    <button onClick={() => stopAccess(lvl.id)}>Arrêter</button>
                  </Show>
                </div>
              );
            }}
          </For>
        </div>
      </div>
    ),
  };

  const rdsTab: SubTab = {
    id: "rds",
    label: "RDS multi-session",
    render: () => {
      const mode = () => policy()?.rdsTargetMode;
      // Lot 4-bis : énumération réelle via heartbeat host (WTSEnumerateSessionsEx).
      const sessions = () => ws.state.rdsSessions ?? [];
      // Prise de console : sessions occupées par un utilisateur (console OU RDP).
      const occupiedSessions = () => sessions().filter((s) => Boolean(s.userName));
      return (
        <div class="policy-panel">
          {/* Mode RDS sécurisé (WSS dans RDS, PAS shadow, PAS mstsc) — l'agent ouvre
              un RDP HEADLESS en LOCAL (client IronRDP → 127.0.0.1, NLA locale) avec
              les creds saisis → session RDS isolée (bureau virtuel, marche sans
              écran), capturée par le pipeline WSS re-ciblé (WTSQueryUserToken). */}
          <div class="policy-card">
            <h3>Mode RDS sécurisé — RDP loopback (WSS)</h3>
            <p style={{ "font-size": "12px", color: "var(--text-dim)" }}>
              L'agent ouvre une <strong>session RDS isolée</strong> sur le serveur (RDP headless en local, NLA locale)
              avec les identifiants saisis ; la vue passe par le flux vidéo WSS. Marche{" "}
              <strong>sans écran physique</strong> (bureau virtuel).
            </p>
            <button
              class="btn btn--sm btn--primary"
              disabled={!ws.state.nativeAgentOnline}
              onClick={openSecureRds}
              style={{ padding: "4px 12px", "font-size": "12px" }}
              title="RDP headless loopback (rds_loopback) — session RDS isolée, capture WSS"
            >
              Ouvrir une session sécurisée (RDP loopback)
            </button>
            {/* Le sélecteur VOLT est maintenant intégré au dialog via <CredentialInput>. */}
            <Show when={unattendedError()}>
              <p style={{ "font-size": "12px", color: "#fca5a5", margin: "8px 0 0" }}>{unattendedError()}</p>
            </Show>
          </div>
          {/* Prise de console — libérer l'écran d'un utilisateur déjà présent.
              Toujours visible (indépendant de rdsTargetMode) car cible la session
              console/active réellement occupée. */}
          <Show when={occupiedSessions().length > 0}>
            <div class="policy-card">
              <h3>Prendre la console — libérer l'écran</h3>
              <p style={{ "font-size": "12px", color: "var(--text-dim)" }}>
                Un utilisateur occupe l'écran. <strong>Verrouiller</strong> préserve sa session (reconnectable) ;{" "}
                <strong>Fermer</strong> la déconnecte définitivement. Puis ouvrez votre propre session sur la console.
              </p>
              <For each={occupiedSessions()}>
                {(s: RdsSession) => (
                  <div class="policy-row">
                    <span class="key">
                      #{s.sessionId} {s.isConsole ? <em>(console)</em> : s.sessionName}
                    </span>
                    <span class="value">
                      {s.domainName ? `${s.domainName}\\` : ""}
                      {s.userName}
                    </span>
                    <span class={`source ${s.state === "active" ? "registry" : "default"}`}>
                      {s.state}
                      {s.locked ? " · verrouillée" : ""}
                    </span>
                    <button
                      disabled={consoleActionBusy() !== null}
                      onClick={() => freeConsole(s, "lock")}
                      style={{ "margin-left": "8px", padding: "2px 8px", "font-size": "11px" }}
                      title="WTSDisconnectSession — préserve la session, libère l'écran"
                    >
                      {consoleActionBusy() === s.sessionId ? "..." : "Verrouiller"}
                    </button>
                    <button
                      class="danger"
                      disabled={consoleActionBusy() !== null}
                      onClick={() => freeConsole(s, "logoff")}
                      style={{ "margin-left": "4px", padding: "2px 8px", "font-size": "11px" }}
                      title="WTSLogoffSession — ferme définitivement la session"
                    >
                      {consoleActionBusy() === s.sessionId ? "..." : "Fermer"}
                    </button>
                  </div>
                )}
              </For>
              <Show when={consoleActionMsg()}>
                <div
                  style={{
                    "font-size": "12px",
                    "margin-top": "8px",
                    color: consoleActionError() ? "var(--danger, #e55)" : "var(--ok, #4a4)",
                  }}
                >
                  {consoleActionMsg()}
                </div>
              </Show>
            </div>
          </Show>
          <Show
            when={mode() && mode() !== "consoleOnly"}
            fallback={
              <div class="policy-empty">
                Cible figée à la session console (<code>rdsTargetMode = {mode() ?? "non défini"}</code>). Le panel
                ciblera la session console.
              </div>
            }
          >
            <Show
              when={mode() === "explicit"}
              fallback={
                <div class="policy-empty">
                  Cible figée à la session console (<code>rdsTargetMode = {mode() ?? "consoleOnly"}</code>). Pas de
                  sélection multi-session.
                </div>
              }
            >
              <Show
                when={sessions().length > 0}
                fallback={
                  <div class="policy-empty">
                    Aucune session RDS reportée par l'hôte.
                    <br />
                    <small>
                      Soit l'hôte n'a pas Terminal Services, soit la version agent est antérieure au Lot 4-bis (pas
                      d'énumération <code>WTSEnumerateSessionsEx</code> publiée).
                    </small>
                  </div>
                }
              >
                <div class="policy-card">
                  <h3>Sessions Terminal Services ({sessions().length})</h3>
                  <For each={sessions()}>
                    {(s: RdsSession) => (
                      <div class="policy-row">
                        <span class="key">
                          #{s.sessionId} {s.sessionName} {s.isConsole ? <em>(console)</em> : null}
                        </span>
                        <span class="value">
                          {s.domainName ? `${s.domainName}\\` : ""}
                          {s.userName || "—"}
                        </span>
                        <span class={`source ${s.state === "active" ? "registry" : "default"}`}>{s.state}</span>
                        <button
                          disabled={s.state !== "active" || !ws.state.supportApproved}
                          onClick={() =>
                            ws.send({ type: "rds:select_session", sessionId: s.sessionId } as unknown as Parameters<
                              typeof ws.send
                            >[0])
                          }
                          style={{ "margin-left": "8px", padding: "2px 8px", "font-size": "11px" }}
                          title="Mode helper_inject : respawn Helper dans cette session"
                        >
                          Cibler
                        </button>
                        <Show when={policy()?.rdpShadowMode === "credential_prompt"}>
                          <button
                            disabled={s.state !== "active" || !ws.state.supportApproved}
                            onClick={() => openShadowDialog(s.sessionId)}
                            style={{ "margin-left": "4px", padding: "2px 8px", "font-size": "11px" }}
                            title="Mode credential_prompt : LogonUser admin + mstsc /shadow"
                          >
                            Shadow (creds)
                          </button>
                        </Show>
                      </div>
                    )}
                  </For>
                  <div style={{ "font-size": "11px", color: "var(--text-dim)", "margin-top": "8px" }}>
                    La sélection effective (envoi de l'ID au host) sera branchée dans une livraison ultérieure — pour
                    l'instant l'agent cible la session console.
                  </div>
                </div>
              </Show>
            </Show>
          </Show>
        </div>
      );
    },
  };

  return (
    <>
      <PanelLayout title="Politiques hôte" subtabs={[policyTab, accessTab, rdsTab]} status={status()} />

      {/* Lot 6 — Dialog credentials pour mode RDP Shadow credential_prompt */}
      <Show when={shadowDialogOpen()}>
        <div class="policy-shadow-backdrop" onClick={() => setShadowDialogOpen(false)}>
          <div class="policy-shadow-dialog" onClick={(e) => e.stopPropagation()}>
            <h3>Shadow RDP — session #{shadowTargetSession()}</h3>
            <p style={{ "font-size": "12px", color: "var(--text-dim)" }}>
              Credentials admin pour <code>LogonUser</code> + <code>mstsc /shadow</code>.
              <br />
              Jamais persistés. Validé contre <code>{policy()?.rdpShadowAllowedGroups || "Administrators"}</code>.
            </p>
            <CredentialInput
              usage="rdp"
              vaultDefaults={props.vaultDefaults}
              layout="stacked"
              fields={{ username: true, password: true, domain: "optional" }}
              defaults={{ domain: "." }}
              requestAgentKey={requestCredentialKey}
              onResult={(r) => setShadowResult(r)}
            />
            <label style={{ display: "flex", "align-items": "center", gap: "6px" }}>
              <input
                type="checkbox"
                checked={shadowControl()}
                onChange={(e) => setShadowControl(e.currentTarget.checked)}
              />
              Contrôle clavier/souris (sinon vue seule)
            </label>
            <Show when={shadowError()}>
              <div class="policy-shadow-error">{shadowError()}</div>
            </Show>
            <div class="policy-shadow-actions">
              <button onClick={() => setShadowDialogOpen(false)}>Annuler</button>
              <button
                disabled={!shadowResult().ready && !(shadowResult().mode === "volt" && shadowResult().credentialId)}
                onClick={submitShadow}
                style={{ background: "var(--accent)", color: "white" }}
              >
                Démarrer le shadow
              </button>
            </div>
          </div>
        </div>
      </Show>

      <Show when={shadowActiveId()}>
        <div class="policy-shadow-active">
          Shadow actif (<code>{shadowActiveId()?.slice(0, 8)}…</code>) —
          <button onClick={stopShadow} style={{ "margin-left": "8px" }}>
            Arrêter
          </button>
        </div>
      </Show>

      {/* Lot 7 — Dialog credentials Unattended Access */}
      <Show when={unattendedDialogOpen()}>
        <div
          class="policy-shadow-backdrop"
          onClick={() => {
            setUnattendedDialogOpen(false);
            setSecureRdsMode(false);
          }}
        >
          <div class="policy-shadow-dialog" onClick={(e) => e.stopPropagation()}>
            <h3>Accès non assisté — credentials admin</h3>
            <p style={{ "font-size": "12px", color: "var(--text-dim)" }}>
              <code>LogonUser</code> + <code>CreateProcessAsUser</code> spawn le Helper dans une session interactive
              éphémère.
              <br />
              Jamais persistés. Validé contre <code>{policy()?.unattendedAccessAllowedGroups || "Administrators"}</code>
              . Logoff total à l'arrêt.
            </p>
            <CredentialInput
              usage="rdp"
              vaultDefaults={props.vaultDefaults}
              layout="stacked"
              fields={{ username: true, password: true, domain: "optional" }}
              defaults={{ domain: "." }}
              requestAgentKey={requestCredentialKey}
              onResult={(r) => setUnattendedResult(r)}
            />
            <Show when={unattendedError()}>
              <div class="policy-shadow-error">{unattendedError()}</div>
            </Show>
            <div class="policy-shadow-actions">
              <button
                onClick={() => {
                  setUnattendedDialogOpen(false);
                  setSecureRdsMode(false);
                }}
              >
                Annuler
              </button>
              <button
                disabled={
                  !unattendedResult().ready && !(unattendedResult().mode === "volt" && unattendedResult().credentialId)
                }
                onClick={submitUnattended}
                style={{ background: "var(--accent)", color: "white" }}
              >
                Ouvrir session
              </button>
            </div>
          </div>
        </div>
      </Show>
    </>
  );
};

registerPanel("policy", PolicyPanel);

export default PolicyPanel;
